/* biome-ignore-all lint/suspicious/noExplicitAny: reading content page */
import type React from "react"
import { useState, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button } from "antd"
import { ReloadOutlined } from "@ant-design/icons"
import { IconBook2 } from "@tabler/icons-react"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"
import { readingContentService } from "@/services/dictionary/readingContentApi"
import { ReadingContentFilterForm, type ReadingContentFilterValues } from "./components/ReadingContentFilterForm"
import { ReadingContentTable } from "./components/ReadingContentTable"
import { ReadingContentDetailModal } from "./components/ReadingContentDetailModal"
import type { ReadingSetItem, ReadingContentItem } from "./types"

export interface ReadingContentQueryParams {
  page: number
  per_page: number
  search?: string
  type?: string
  difficulty?: string
  status?: string
}

export const ReadingContentPage: React.FC = () => {
  const [searchParams, setSearchParams] = useTableParams<ReadingContentQueryParams>({
    ...PAGE_CONFIG,
    search: "",
    type: undefined,
    difficulty: undefined,
    status: undefined,
  })

  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<ReadingContentItem | null>(null)
  const [modalItems, setModalItems] = useState<ReadingContentItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState<number>(0)

  // Fetch sets with reading query
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["reading_contents_list", searchParams],
    queryFn: () =>
      readingContentService.list({
        page: searchParams.page,
        limit: searchParams.per_page,
        search: searchParams.search ? searchParams.search.trim() : undefined,
        type: searchParams.type || undefined,
        difficulty: searchParams.difficulty || undefined,
        status: searchParams.status || undefined,
      }),
  })

  const handleSearch = (values: ReadingContentFilterValues) => {
    setSearchParams({
      ...searchParams,
      search: values.search,
      type: values.type,
      difficulty: values.difficulty,
      status: values.status,
      page: 1,
    })
  }

  const handleViewDetail = (readingItem: ReadingContentItem, index: number, list: ReadingContentItem[]) => {
    setSelectedRecord(readingItem)
    setSelectedIndex(index)
    setModalItems(list)
    setDetailModalOpen(true)
  }

  const handleModalNavigate = (index: number) => {
    if (modalItems[index]) {
      setSelectedRecord(modalItems[index])
      setSelectedIndex(index)
    }
  }

  const items = (data?.data || []) as ReadingSetItem[]
  const totalSets = data?.total ?? 0

  const totalReadings = useMemo(() => {
    return items.reduce((acc, curr) => acc + (curr.reading_count || 0), 0)
  }, [items])

  return (
    <div className="flex flex-col gap-2">
      {/* 1. Filter form */}
      <ReadingContentFilterForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          type: searchParams.type,
          difficulty: searchParams.difficulty,
          status: searchParams.status,
        }}
      />

      {/* 2. Main Data Card & Table */}
      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              <IconBook2 className="text-blue-500" size={20} />
              <span className="font-semibold text-base text-gray-800">
                Danh Sách Bài Đọc Theo Bộ Từ (Reading Contents)
              </span>
              <Divider orientation="vertical" />
              <span className="font-normal text-gray-500 text-sm">
                Tổng số bộ: <strong className="text-gray-700">{totalSets}</strong> | Tổng bài reading hiện tại:{" "}
                <strong className="text-blue-600">{totalReadings}</strong>
              </span>
            </div>
            <Button icon={<ReloadOutlined spin={isFetching} />} onClick={() => refetch()} loading={isFetching}>
              Làm mới
            </Button>
          </div>
        }
      >
        <ReadingContentTable
          data={items}
          loading={isFetching}
          onViewDetail={handleViewDetail}
          pagination={{
            ...PAGINATION_CONFIG,
            current: searchParams.page,
            pageSize: searchParams.per_page,
            total: totalSets,
            onChange: (page: number, pageSize: number) => {
              setSearchParams({ ...searchParams, page, per_page: pageSize })
            },
          }}
        />
      </Card>

      {/* 3. Detail modal */}
      <ReadingContentDetailModal
        open={detailModalOpen}
        record={selectedRecord}
        items={modalItems}
        currentIndex={selectedIndex}
        onNavigate={handleModalNavigate}
        onClose={() => {
          setDetailModalOpen(false)
          setSelectedRecord(null)
        }}
      />
    </div>
  )
}

export default ReadingContentPage
